import { useEffect, useState } from 'react';
import { isUpdateAvailable } from '../lib/appUpdate.js';

/**
 * Live-load apps don't auto-update while the WebView stays warm. This watches
 * for a newer deploy when the app returns to the foreground (and every 15 min
 * while open) and offers a Refresh, so members get updates without a manual
 * force-quit. It never force-reloads on its own (that could interrupt an
 * in-progress set), so the member taps Refresh when it's safe.
 */
export default function UpdatePrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    async function check() {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (await isUpdateAvailable() && alive) setShow(true);
    }
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(check, 15 * 60 * 1000);
    check();
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(id);
    };
  }, []);

  if (!show) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        bottom: 'calc(16px + env(safe-area-inset-bottom))',
        left: 16, right: 16, zIndex: 95,
        background: 'var(--bg-elev)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-lg)',
        padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Update available</div>
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>A newer version of TrainForge is ready.</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="primary" onClick={() => window.location.reload()} style={{ padding: '8px 16px', minHeight: 0, fontSize: 12 }}>Refresh</button>
        <button onClick={() => setShow(false)} style={{ padding: '8px 12px', minHeight: 0, fontSize: 12 }}>Later</button>
      </div>
    </div>
  );
}
